/**
 * Thin Preact hook over the pure `paceAutoWalk` reducer (bd aa14). Supplies the
 * real clock and schedules a wake-up when each step's dwell elapses, so the
 * shopper-facing auto-walk advances at a humane pace regardless of how fast the
 * flow actually runs. All pacing logic lives in (and is tested via) paceAutoWalk.
 */
import { useEffect, useState } from 'preact/hooks';
import type { NarratedAutoWalkStep } from './NarratedAutoWalkProgress.js';
import { type PaceResult, type PaceState, initialPaceState, paceAutoWalk } from './paceAutoWalk.js';

function clockMs(): number {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}

export type PacedAutoWalk = Pick<PaceResult, 'completed' | 'running' | 'pending' | 'reveal'>;

export function usePacedAutoWalk(
  liveSteps: NarratedAutoWalkStep[],
  busy: boolean,
  minStepMs: number,
): PacedAutoWalk {
  const [state, setState] = useState<PaceState>(initialPaceState);
  const result = paceAutoWalk(state, { liveSteps, busy, minStepMs, nowMs: clockMs() });

  const changed =
    result.state.shown !== state.shown ||
    result.state.seen.length !== state.seen.length ||
    result.state.currentSinceMs !== state.currentSinceMs;

  // Commit an advance (re-render once). Guarded so it can't loop: after commit
  // the next render recomputes `changed === false` and stops.
  useEffect(() => {
    if (changed) setState(result.state);
  });

  // Wake up when the current step's dwell is due so the display advances on time.
  useEffect(() => {
    if (result.nextTickMs == null) return;
    const id = setTimeout(() => setState((s) => ({ ...s })), result.nextTickMs + 16);
    return () => clearTimeout(id);
  }, [result.nextTickMs]);

  return {
    completed: result.completed,
    running: result.running,
    pending: result.pending,
    reveal: result.reveal,
  };
}
